import {noop} from '@benchling/js-utils';
import produce from 'immer';
import React, {useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {
  createJellyfish,
  CreateJellyfishParams,
  CreateJellyfishResponse,
  CreateTentacleParams,
} from 'app-core/archetype/JellyfishDAO';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {BaseCreateModalContents} from 'folder-items/BaseCreateModal';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {getStore} from 'redux-stores/Store';
import {maybeRefreshListing} from 'search/SearchUIReduxActions';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import EditableTentaclesTable, {CreatableTentacle} from '../components/EditableTentaclesTable';

export const NEW_TENTACLE: CreatableTentacle = {
  id: 'blank',
  name: '',
};

async function onCreateJellyfish(data: CreateJellyfishParams): Promise<CreateJellyfishResponse> {
  const response = await createJellyfish(data);
  FileBrowserActions.openFile(response.createdIds[0]);
  getStore().dispatch(maybeRefreshListing(Section.JELLYFISH));
  return response;
}

export interface CreateJellyfishModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

function validateCreateJellyfishParams(createJellyfish: {
  name: string;
  folderId?: string | null;
  tentacles: Array<CreateTentacleParams>;
}): createJellyfish is CreateJellyfishParams {
  return (
    createJellyfish.name.length > 0 &&
    createJellyfish.folderId != null &&
    createJellyfish.tentacles != null &&
    createJellyfish.tentacles.every(({name}) => name.length > 0)
  );
}

export default function CreateJellyfishModal(props: CreateJellyfishModalProps): JSX.Element {
  const [name, setName] = useState<string>('Untitled');
  const [folderId, setFolderId] = useState(FolderDefaults.get(FolderDefaultsKey.JELLYFISH));
  const [tentacles, setTentacles] = useState<Array<CreatableTentacle>>([]);

  const createJellyfishDraft = {
    name,
    folderId,
    tentacles: tentacles.map(({name}) => ({name})),
    description: null, // TODO(BNCH-85231): Add description to Jellyfish UI
  };

  const handleSubmit = async (): Promise<void> => {
    assert(validateCreateJellyfishParams(createJellyfishDraft), 'Errors in create jellyfish params');
    await withLoadingScreen(onCreateJellyfish(createJellyfishDraft), 'Creating jellyfish...');
    props.onRequestSubmit();
    FolderDefaults.set(FolderDefaultsKey.JELLYFISH, createJellyfishDraft.folderId);
  };

  return (
    <Modal
      buttons={[
        {
          disabled: !validateCreateJellyfishParams(createJellyfishDraft),
          submit: true,
          text: 'Create',
          style: ButtonStyle.SECONDARY,
        },
      ]}
      title={<span>Create jellyfish</span>}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
    >
      <BaseCreateModalContents
        name={name}
        setName={setName}
        modelType={ModelTypeConstants.JELLYFISH}
        folderId={folderId}
        setFolderId={setFolderId}
        showInventory={true}
        setTemplateCollection={noop}
        tagSchema={null}
        updateTagSchema={noop}
        updateSchemaTag={noop}
        schemaTags={[]}
        errorsByFieldId={{}}
      />
      <EditableTentaclesTable
        tentacles={tentacles}
        onUpdateTentacles={setTentacles}
        onAddTentacle={() => {
          setTentacles(
            produce(tentacles, (draft) => {
              draft.push({...NEW_TENTACLE, id: uuidv4()});
            })
          );
        }}
        onRemoveTentacle={(id: string) => {
          const tentacleIndex = tentacles.findIndex((tentacle) => tentacle.id === id);
          assert(tentacleIndex !== -1, `Couldn't find tentacle with id '${id}'`);
          setTentacles(
            produce(tentacles, (draft) => {
              draft.splice(tentacleIndex, 1);
            })
          );
        }}
        onUpdateTentacle={(id: string, update: Partial<CreatableTentacle>) => {
          const tentacleIndex = tentacles.findIndex((tentacle) => tentacle.id === id);
          assert(tentacleIndex !== -1, `Couldn't find tentacle with id '${id}'`);
          setTentacles(
            produce(tentacles, (draft) => {
              draft[tentacleIndex] = {...draft[tentacleIndex], ...update};
            })
          );
        }}
      />
    </Modal>
  );
}
